import React, { useEffect, useMemo, useState, useRef } from 'react';
import ProgressBar from '../components/ProgressBar';
import ProductVsFeedChart from '../components/ProductVsFeedChart';
import MonthlyConsumptionChart from '../components/MonthlyConsumptionChart';
import ProductCVBarChart from '../components/ProductCVBarChart';
import { CalendarIcon } from '@heroicons/react/solid';
import { format } from 'date-fns';
import DateRangePicker from '../components/DateRangePicker';
import DowntimeDonutChart from '../components/DowntimeDonutChart';

const columns = [
  { key: 'created_at',   label: 'Date & Time' },
  { key: 'operator',     label: 'Operator' },
  { key: 'downtime_type',label: 'Type' },
  { key: 'downtime_stop', label: 'Plant Stop' },
  { key: 'downtime_start',label: 'Plant Start' },
  { key: 'downtime_total_hours', label: 'Duration' },
  { key: 'system_affected',     label: 'System Affected' },
  { key: 'downtime_description',label: 'Description' },
  { key: 'downtime_details',    label: 'Details' },
];

const formatDuration = hours => {
  if (hours == null) return '';
  const totalMinutes = Math.round(parseFloat(hours) * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const hPart = h > 0 ? `${h}h` : '';
  const mPart = m > 0 ? `${m}m` : '';
  return `${hPart}${hPart && mPart ? ' ' : ''}${mPart || (hPart ? '' : '0m')}`;
};

function MonthToDateProduction() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [rangeLabel,    setRangeLabel]    = useState('');
  const [data,          setData]          = useState([]);
  const [sortConfig,    setSortConfig]    = useState({ key: null, direction: null });
  const [utilisation,   setUtilisation]   = useState(0);
  const [prevUtil,      setPrevUtil]      = useState(0);
  const [availability,  setAvailability]  = useState(0); // engineering
  const [prevAvail,     setPrevAvail]     = useState(0); // engineering previous
  const [availabilityProcess, setAvailabilityProcess] = useState(0);
  const [prevAvailProcess, setPrevAvailProcess] = useState(0);
  const [showPicker,    setShowPicker]    = useState(false);
  const defaultStart = useRef(startDate);
  const defaultEnd = useRef(endDate);
  const [showDefaultBtn, setShowDefaultBtn] = useState(false);
  const [fadeDefaultBtn, setFadeDefaultBtn] = useState(false);
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    const fmt = d =>
      d.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    setRangeLabel(`${fmt(startDate)} - ${fmt(endDate)}`);
  }, [startDate, endDate]);

  const toLocalIso = d => {
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 19);
  };

  const parsePickerValue = value => {
    if (!value) return null;
    const normalized = value.length === 16 ? `${value}:00` : value;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const toBackendDateTime = d => format(d, 'yyyy-MM-dd HH:mm:ss');

  useEffect(() => {
    const startParam = toBackendDateTime(startDate);
    const endParam = toBackendDateTime(endDate);
    fetch(
      `/api/downtime-table.php?start=${encodeURIComponent(startParam)}&end=${encodeURIComponent(endParam)}`
    )
      .then(res => res.json())
      .then(res => setData(res.data || []))
      .catch(() => setData([]));
  }, [startDate, endDate]);

  useEffect(() => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');

    if (isCustom) {
      fetch(
        `/api/monthly-plant-stats.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`
      )
        .then(res => res.json())
        .then(currRes => {
          const utilVal = Number(currRes.plant_utilisation);
          const availVal = Number(currRes.availability);
          const availProcVal = Number(currRes.availability_process);
          setUtilisation(Number.isFinite(utilVal) ? utilVal : null);
          setAvailability(Number.isFinite(availVal) ? availVal : null);
          setAvailabilityProcess(Number.isFinite(availProcVal) ? availProcVal : null);
          setPrevUtil(null);
          setPrevAvail(null);
          setPrevAvailProcess(null);
        })
        .catch(err => {
          console.error('Error fetching monthly plant stats', err);
          setUtilisation(null);
          setAvailability(null);
          setAvailabilityProcess(null);
          setPrevUtil(null);
          setPrevAvail(null);
          setPrevAvailProcess(null);
        });
    } else {
      const rangeMs = end.getTime() - start.getTime() + 1000;
      const prevStart = new Date(start.getTime() - rangeMs);
      prevStart.setHours(0, 0, 0, 0);
      const prevEnd = new Date(end.getTime() - rangeMs);
      prevEnd.setHours(23, 59, 59, 999);

      const prevStartStr = format(prevStart, 'yyyy-MM-dd');
      const prevEndStr = format(prevEnd, 'yyyy-MM-dd');

      console.log('prevStartStr', prevStartStr, 'prevEndStr', prevEndStr, 'startStr', startStr, 'endStr', endStr);

      Promise.all([
        fetch(`/api/monthly-plant-stats.php?start=${encodeURIComponent(prevStartStr)}&end=${encodeURIComponent(prevEndStr)}`).then(res => res.json()),
        fetch(`/api/monthly-plant-stats.php?start=${encodeURIComponent(startStr)}&end=${encodeURIComponent(endStr)}`).then(res => res.json()),
      ])
        .then(([prevRes, currRes]) => {
          console.log('prevRes', prevRes, 'currRes', currRes);
          const prevUtilVal = Number(prevRes.plant_utilisation);
          const prevAvailVal = Number(prevRes.availability);
          const prevAvailProcVal = Number(prevRes.availability_process);
          const utilVal = Number(currRes.plant_utilisation);
          const availVal = Number(currRes.availability);
          const availProcVal = Number(currRes.availability_process);
          setPrevUtil(Number.isFinite(prevUtilVal) ? prevUtilVal : null);
          setPrevAvail(Number.isFinite(prevAvailVal) ? prevAvailVal : null);
          setPrevAvailProcess(Number.isFinite(prevAvailProcVal) ? prevAvailProcVal : null);
          setUtilisation(Number.isFinite(utilVal) ? utilVal : null);
          setAvailability(Number.isFinite(availVal) ? availVal : null);
          setAvailabilityProcess(Number.isFinite(availProcVal) ? availProcVal : null);
        })
        .catch(err => {
          console.error('Error fetching monthly plant stats', err);
          setPrevUtil(null);
          setPrevAvail(null);
          setPrevAvailProcess(null);
          setUtilisation(null);
          setAvailability(null);
          setAvailabilityProcess(null);
        });
    }
  }, [startDate, endDate, isCustom]);


  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a,b) => {
      const v1 = a[sortConfig.key], v2 = b[sortConfig.key];
      if (v1 == null) return 1;
      if (v2 == null) return -1;
      if (v1 === v2) return 0;
      return sortConfig.direction === 'asc'
        ? (v1 < v2 ? -1 : 1)
        : (v1 < v2 ? 1 : -1);
    });
  }, [data, sortConfig]);

  const totalDowntime = useMemo(() => {
    return data.reduce((sum, row) => sum + (parseFloat(row.downtime_total_hours) || 0), 0);
  }, [data]);

  const downtimeTotals = useMemo(() => {
    const categories = [
      'Engineering Downtime',
      'External Downtime',
      'Planned Maintenance',
      'Process Downtime',
    ];
    const totals = categories.map(label => ({ label, value: 0 }));
    const idxMap = Object.fromEntries(categories.map((label, i) => [label, i]));
    data.forEach(row => {
      const idx = idxMap[row.downtime_type];
      if (idx !== undefined) {
        totals[idx].value += parseFloat(row.downtime_total_hours) || 0;
      }
    });
    return totals;
  }, [data]);

  const handleSort = key => {
    setSortConfig(curr => {
      if (curr.key !== key)           return { key, direction: 'desc' };
      if (curr.direction === 'desc')  return { key, direction: 'asc' };
      return { key: null, direction: null };
    });
  };

  const prevMonth = () => {
    const step = endDate.getTime() - startDate.getTime() + 1000;
    setStartDate(new Date(startDate.getTime() - step));
    setEndDate(new Date(endDate.getTime() - step));
    setIsCustom(false);
  };
  const nextMonth = () => {
    const step = endDate.getTime() - startDate.getTime() + 1000;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const newEnd = new Date(endDate.getTime() + step);
    if (newEnd <= today) {
      setStartDate(new Date(startDate.getTime() + step));
      setEndDate(newEnd);
      setIsCustom(false);
    }
  };
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const disableNext = endDate.getTime() >= todayEnd.getTime();

  const startStr = toLocalIso(startDate);
  const endStr = toLocalIso(endDate);

  const resetToDefault = () => {
    setStartDate(new Date(defaultStart.current));
    setEndDate(new Date(defaultEnd.current));
    setIsCustom(false);
  };

  useEffect(() => {
    const isDefault =
      startDate.getTime() === defaultStart.current.getTime() &&
      endDate.getTime() === defaultEnd.current.getTime();
    if (!isDefault) {
      setShowDefaultBtn(true);
    } else if (showDefaultBtn) {
      setFadeDefaultBtn(true);
      const t = setTimeout(() => {
        setShowDefaultBtn(false);
        setFadeDefaultBtn(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [startDate, endDate, showDefaultBtn]);

  return (
    <div className="p-4 space-y-4 text-white flex flex-col">
      {/* header */}
      <div className="flex items-center justify-center border-b border-gray-600 pb-2">
        <div className="flex items-center space-x-2">
          {showDefaultBtn && (
            <button
              onClick={resetToDefault}
              className={`px-2 py-1 bg-[#00D04C] rounded shadow hover:bg-[#00B240] transition ease-in-out duration-200 transition-opacity ${fadeDefaultBtn ? 'opacity-0' : 'opacity-100'}`}
            >
              Default
            </button>
          )}
          <button
            onClick={prevMonth}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] transition ease-in-out duration-200"
            aria-label="Previous Month"
          >{'<'}</button>
          <div
            className="px-3 py-1 bg-[#0d2c3d] rounded shadow flex items-center space-x-1 transition transform hover:scale-105 cursor-pointer"
            title="Month view – click arrows to navigate"
            onClick={() => setShowPicker(true)}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="text-sm">{rangeLabel}</span>
          </div>
          <button
            onClick={nextMonth}
            disabled={disableNext}
            className="px-2 py-1 bg-[#0d2c3d] rounded shadow hover:bg-[#123b52] disabled:opacity-50 transition ease-in-out duration-200"
            aria-label="Next Month"
          >{'>'}</button>
        </div>
      </div>

      {showPicker && (
        <DateRangePicker
          initialStart={toLocalIso(startDate)}
          initialEnd={toLocalIso(endDate)}
          onApply={(s, e) => {
            const start = parsePickerValue(s);
            const end = parsePickerValue(e);
            if (!start || !end) return;
            setStartDate(start);
            setEndDate(end);
            setIsCustom(true);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}

      {/* grid */}
      <div className="grid grid-cols-4 gap-4 auto-rows-max">
        {/* Row 1 */}
        <div className="col-span-4 grid grid-cols-3 gap-4">
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
            <h2 className="text-center text-sm mb-2">Plant Utilisation</h2>
            <div className="mt-4 space-y-4">
              {isCustom ? (
                <ProgressBar label="Custom Utilisation" value={utilisation} target={95} />
              ) : (
                <>
                  <ProgressBar label="Selected Month Utilisation" value={utilisation} target={95} />
                  <ProgressBar label="Previous Month Utilisation" value={prevUtil} barColor="bg-blue-600" target={95} />
                </>
              )}
            </div>
          </div>
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
            <h2 className="text-center text-sm mb-2">Plant Availability: Engineering</h2>
            <div className="mt-4 space-y-4">
              {isCustom ? (
                <ProgressBar label="Custom Availability: Eng." value={availability} target={95} />
              ) : (
                <>
                  <ProgressBar label="Selected Month Availability: Eng." value={availability} target={95} />
                  <ProgressBar label="Previous Month Availability: Eng." value={prevAvail} barColor="bg-blue-600" target={95} />
                </>
              )}
            </div>
          </div>
          <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
            <h2 className="text-center text-sm mb-2">Plant Availability: Process</h2>
            <div className="mt-4 space-y-4">
              {isCustom ? (
                <ProgressBar label="Custom Availability: Process" value={availabilityProcess} target={95} />
              ) : (
                <>
                  <ProgressBar label="Selected Month Availability: Process" value={availabilityProcess} target={95} />
                  <ProgressBar label="Previous Month Availability: Process" value={prevAvailProcess} barColor="bg-blue-600" target={95} />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Row 2 */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="text-center text-sm">Product Tons vs. Feed to Plant</h2>
          <ProductVsFeedChart start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="text-center text-sm">Product Quality – CV (MJ/kg)</h2>
          <ProductCVBarChart start={startStr} end={endStr} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="text-center text-sm">Monthly Magnetite Consumption (kg/ton)</h2>
          <MonthlyConsumptionChart type="magnetite" start={startStr} end={endStr} isCustom={isCustom} />
        </div>
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col h-full">
          <h2 className="text-center text-sm">Monthly Flocculant Consumption (g/ton)</h2>
          <MonthlyConsumptionChart type="flocculant" start={startStr} end={endStr} isCustom={isCustom} />
        </div>

        {/* Row 3 */}
        <div className="bg-[#0d2c3d] shadow-lg rounded-lg p-4 flex flex-col col-span-4 h-[400px]">
          <div className="flex justify-between text-sm mb-2">
            <h2>
              Down Time Table: <span className="font-normal">{sortedData.length}</span>
            </h2>
            <div>
              Total Downtime: <span className="font-normal">{formatDuration(totalDowntime)}</span>
            </div>
          </div>
          <div className="flex-1 flex overflow-hidden">
            <div className="w-1/3 flex flex-col mr-4">
              <DowntimeDonutChart totals={downtimeTotals} total={totalDowntime} />
            </div>
            <div className="flex-1 overflow-auto">
              <table className="min-w-full text-xs text-center overflow-hidden rounded-lg">
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="px-2 py-1 bg-green-700 cursor-pointer sticky top-0"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((row, idx) => (
                    <tr key={idx} className="even:bg-[#0f3346]">
                      {columns.map(col => (
                        <td
                          key={col.key}
                          className="px-2 py-1 border-b border-gray-700"
                        >
                          {col.key === 'downtime_total_hours'
                            ? formatDuration(row[col.key])
                            : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MonthToDateProduction;
